import type { MouseEvent } from 'react';
import { Button, Tooltip } from '@servicetitan/anvil2';
import CloseIcon from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import { getTestIdProps } from '@lib/testUtils';
import { inboxLocators } from '@locators/inbox';
import useTaskActions from '@modules/TasksModule/hooks/useTaskActions';
import { TaskVoiceCardAction } from '../action-utils';
import { useCloseEndedCallHandler } from './useCloseEndedCallHandler';

type Props = {
  taskId: string;
  action?: TaskVoiceCardAction | null;
  /** The card's name, so each X is named for its card. */
  name?: string;
};

const labelOf = (verb: string, name?: string) =>
  name ? `${verb} ${name}` : verb;

type CardXProps = {
  label: string;
  testId: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

/** An icon-only X, with a tooltip that reads its label, as Anvil2 asks. */
const CardX = ({ label, testId, onClick }: CardXProps) => (
  <Tooltip placement='top'>
    <Tooltip.Trigger>
      <Button
        appearance='ghost'
        size='small'
        icon={CloseIcon}
        aria-label={label}
        onClick={onClick}
        {...getTestIdProps(testId)}
      />
    </Tooltip.Trigger>
    <Tooltip.Content>{label}</Tooltip.Content>
  </Tooltip>
);

/**
 * The X in a call card's upper-right corner, with the new Assigned to Me
 * column. It replaces Close on ended calls and Dismiss on missed calls, keeps
 * their test IDs, and does exactly what they do. Ringing, live and on-hold
 * cards have no X.
 */
export const TaskVoiceCloseButton = ({ taskId, action, name }: Props) => {
  const { handleComplete } = useTaskActions(taskId);
  const handleClose = useCloseEndedCallHandler(taskId);

  if (action === TaskVoiceCardAction.endedCall) {
    return (
      <CardX
        label={labelOf('Close', name)}
        testId={inboxLocators.taskList.closeButton}
        onClick={handleClose}
      />
    );
  }
  if (action === TaskVoiceCardAction.missedCall) {
    return (
      <CardX
        label={labelOf('Dismiss', name)}
        testId={inboxLocators.taskList.dismissButton}
        onClick={handleComplete}
      />
    );
  }
  return null;
};
